import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { LoadingPanel } from "../../LoadingPanel";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { getCSRFToken } from '../../../app/getCSRFToken';
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Checkbox } from "@progress/kendo-react-inputs";

export const AdministratorMyShortedUnavailableEvents = () => {
    const dispatch = useDispatch();

    const [myShortedUnavailableEvents, setMyShortedUnavailableEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [totalEventCount, setTotalEventCount] = useState(0);
    const [isHeaderCheckboxChecked, setIsHeaderCheckboxChecked] = useState(false);
    const [onlyShowBroadcastedEventsFilterValue, setOnlyShowBroadcastedEventsFilterValue] = useState(true);
    const [lastSelectedPOSEventId, setLastSelectedPOSEventId] = useState(undefined);

    const initialPageState = {
        skip: 0,
        take: 20,
    };
    const [page, setPage] = useState(initialPageState);
    const pageChange = (event) => {
        setPage(event.page);
    };

    const [showTicketTypes, setShowTicketTypes] = useState(false);
    const [ticketTypes, setTicketTypes] = useState(undefined);

    const getMyShortedUnavailableEvents = (onlyShowBroadcastedEvents) => {
        setIsLoading(true);

        if (onlyShowBroadcastedEvents === undefined) {
            onlyShowBroadcastedEvents = onlyShowBroadcastedEventsFilterValue;
        }

        const dataItem = {
            onlyShowBroadcastedEvents: onlyShowBroadcastedEvents,
        };

        fetch(`/api/administrator/pos/myshortedunavailableevents`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setMyShortedUnavailableEvents(result);
                setTotalEventCount(result.length);
                setIsHeaderCheckboxChecked(false);
                setLastSelectedPOSEventId(undefined);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);                
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    const deleteEvent = (posEventId) => {
        if (window.confirm("Are you sure you want to delete this event?") === false) {
            return;
        }

        setIsLoading(true);

        const dataItem = {
            posEventIds: [posEventId],
        };
    
        fetch(`/api/administrator/pos/deleteevents`,
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Event deleted from POS successfully");     
                
                getMyShortedUnavailableEvents();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);   
                
                setIsLoading(false);
            });
    };

    const deleteEvents = (posEventIds) => {
        if (window.confirm("Are you sure you want to delete these events?") === false) {
            return;
        }

        setIsLoading(true);

        const dataItem = {
            posEventIds: posEventIds,
        };
    
        fetch(`/api/administrator/pos/deleteevents`,
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Events deleted from POS successfully");     
                
                getMyShortedUnavailableEvents();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);   
                
                setIsLoading(false);
            });
    };

    const deleteAndHideEvents = (posEventIds, venueEventKeys) => {
        if (window.confirm("Are you sure you want to delete and hide these events?") === false) {
            return;
        }

        setIsLoading(true);

        const dataItem = {
            posEventIds: posEventIds,
            venueEventKeys: venueEventKeys,
        };

        fetch(`/api/administrator/pos/deleteandhideevents`,
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Events deleted and permanently hidden successfully");

                getMyShortedUnavailableEvents();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setIsLoading(false);
            });
    };

    const getGridData = () => {
        return myShortedUnavailableEvents.slice(page.skip, page.take + page.skip);
    };

    const viewTicketTypes = (eventTicketTypes) => {
        setTicketTypes(eventTicketTypes);
        setShowTicketTypes(true);
    };

    const ViewUrlCell = (props) => {  
        const url = props.dataItem[props.field];     

        return (
            <td className={"text-nowrap"}>
                {url ? <Link to={{ pathname: url }} target={"_blank"} rel={"noopener noreferrer"}>View Website</Link> : ""}
            </td>
        );
    };

    const DeleteCell = (props) => {
        const posEventId = props.dataItem.posEventId;

        return (
            <td className="text-nowrap">
                <Button type={"button"} icon="trash" title="Delete" onClick={() => deleteEvent(posEventId)}>
                    Delete
                </Button>
            </td>
        );
    };

    const SelectedCell = (props) => {
        const posEventId = props.dataItem.posEventId;
        const selected = props.dataItem.selected;

        return (
            <td className="text-center">
                <Checkbox onChange={(event) => selectedValueChanged(event, posEventId, !selected)} value={selected} />
            </td>
        );
    };

    const SelectedHeaderCell = () => {
        return (
            <div className="text-center">
                <Checkbox onChange={headerCheckboxValueChanged} value={isHeaderCheckboxChecked} />
            </div>
        );
    };

    const ViewTicketTypesCell = (props) => {  
        const eventTicketTypes = props.dataItem.ticketTypes;

        return (
            <td className={"text-nowrap"}>
                <Button 
                    fillMode="link" 
                    type={"button"} 
                    onClick={() => viewTicketTypes(eventTicketTypes)}>View ticket types</Button>
            </td>
        );
    };

    const selectedValueChanged = (event, posEventId, selected) => {
        setIsLoading(true);
        
        setTimeout(() => { 
            let myShortedUnavailableEventsCopy = myShortedUnavailableEvents;

            const itemIndex = myShortedUnavailableEventsCopy.findIndex(dataItem =>
                dataItem.posEventId === posEventId
            );

            let isSelectingMultipleItems = false;

            //Shift selection of multiple items            
            if (event.nativeEvent.shiftKey
                && lastSelectedPOSEventId !== undefined
                && lastSelectedPOSEventId !== posEventId) {
                const lastSelectedItemIndex = myShortedUnavailableEventsCopy.findIndex(dataItem =>
                    dataItem.posEventId === lastSelectedPOSEventId
                );

                if (lastSelectedItemIndex >= 0) {
                    isSelectingMultipleItems = true;

                    let lowIndex = -1;
                    let highIndex = -1;

                    if (itemIndex > lastSelectedItemIndex) {
                        lowIndex = lastSelectedItemIndex;
                        highIndex = itemIndex;
                    }
                    else {
                        lowIndex = itemIndex;
                        highIndex = lastSelectedItemIndex;
                    }

                    for (let i = lowIndex; i <= highIndex; i++) {
                        myShortedUnavailableEventsCopy[i].selected = selected;
                    }
                }
            }

            if (isSelectingMultipleItems === false) {
                myShortedUnavailableEventsCopy[itemIndex].selected = selected;
            }

            if (selected === true) {
                setLastSelectedPOSEventId(posEventId);
            }

            setMyShortedUnavailableEvents(myShortedUnavailableEventsCopy);

            setIsHeaderCheckboxChecked(false);
            setIsLoading(false);
        }, 1);  
    };

    const headerCheckboxValueChanged = () => {
        setIsLoading(true);
        
        setTimeout(() => { 
            const checked = !isHeaderCheckboxChecked;
            setIsHeaderCheckboxChecked(checked);

            let myShortedUnavailableEventsCopy = myShortedUnavailableEvents;
        
            myShortedUnavailableEventsCopy.forEach(dataItem =>
                dataItem.selected = false
            );

            if (checked === true) {
                const gridData = getGridData();

                myShortedUnavailableEventsCopy.forEach(dataItem => {
                    const exists = gridData.includes(dataItem);

                    if (exists) {
                        dataItem.selected = true
                    }
                });
            }        

            setMyShortedUnavailableEvents(myShortedUnavailableEventsCopy);

            setLastSelectedPOSEventId(undefined);

            setIsLoading(false);
        }, 1);        
    };

    const deleteSelectedEvents = () => {
        let posEventIds = [];

        myShortedUnavailableEvents.forEach(dataItem => {
            if (dataItem.selected === true) {
                posEventIds.push(dataItem.posEventId);
            }
        });  

        if (posEventIds.length === 0) {
            addNotificationWithTimeout(dispatch, "error", "No events selected");   
            return;
        }

        deleteEvents(posEventIds);
    };

    const deleteAndHideSelectedEvents = () => {
        let posEventIds = [];
        let venueEventKeys = [];

        myShortedUnavailableEvents.forEach(dataItem => {
            if (dataItem.selected === true) {
                posEventIds.push(dataItem.posEventId);

                if (dataItem.venueEventKey) {
                    venueEventKeys.push(dataItem.venueEventKey);
                }
            }
        });

        if (posEventIds.length === 0) {
            addNotificationWithTimeout(dispatch, "error", "No events selected");
            return;
        }

        deleteAndHideEvents(posEventIds, venueEventKeys);
    };

    const onlyShowBroadcastedEventsFilterValueChanged = (event) => {
        const value = event.value;

        setOnlyShowBroadcastedEventsFilterValue(value);
        getMyShortedUnavailableEvents(value);
    }

    useEffect(() => {
        document.title = "My Shorted Unavailable Events";

        getMyShortedUnavailableEvents();
    }, []);

    return (
        <div className="administratorReport">
            {isLoading ? <LoadingPanel /> : <></>}

            <div className="k-form k-form-md">
                <div className="k-form-fieldset container">
                    <div className="row">
                        <div className="col-12">
                            <div className="k-form-field">
                                <Checkbox
                                    label={"Only show broadcasted events"}
                                    onChange={onlyShowBroadcastedEventsFilterValueChanged}
                                    value={onlyShowBroadcastedEventsFilterValue}                                
                                    />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="m-2 k-form-buttons">
                <Button type={"button"} icon="trash" title="Delete Selected Events" onClick={deleteSelectedEvents}>
                    Delete Selected Events
                </Button>

                <Button type={"button"} icon="trash" title="Delete and Permanently Hide Selected Events" onClick={deleteAndHideSelectedEvents}>
                    Delete and Permanently Hide Selected Events
                </Button>
            </div>   

            <Grid
                data={getGridData()}
                resizable={true}
                skip={page.skip}
                take={page.take}
                total={totalEventCount}
                pageable={true}
                onPageChange={pageChange}
                scrollable="none"
            >
                <Column cell={SelectedCell} headerCell={SelectedHeaderCell} />
                <Column cell={DeleteCell} />
                <Column field="ticketingUrl" title={"Website"} cell={ViewUrlCell} />
                <Column title={"Ticket Types"} cell={ViewTicketTypesCell} />
                <Column field="ticketingWebsiteName" title={"Website"} />
                <Column field="eventName" title={"Event"} />
                <Column field="venueName" title={"Venue"} />
                <Column field="eventDate" title={"Date"} />
                <Column field="venueEventStatusTypeName" title={"Status"} />
            </Grid>

            {showTicketTypes === false ? <></> :
                <Dialog
                    title="Ticket Types"
                    onClose={() => setShowTicketTypes(false)}
                    width={"90vw"}
                >
                    <Grid
                        data={ticketTypes}
                        resizable={true}  
                        scrollable="none"
                    >
                        <Column field="name" title={"Name"} />
                        <Column field="status" title={"Status"} />
                        <Column field="price" title={"Price"} format="{0:c}" />
                        <Column field="taxesAndFees" title={"Fees"} format="{0:c}" />
                        <Column field="orderFee" title={"Order Fee"} format="{0:c}" />
                        <Column field="priceCurrencyCode" title={"Currency"} />
                        <Column field="purchaseTicketLimit" title={"Limit"} className="text-center" />
                        <Column field="minimumAge" title={"Min Age"} className="text-center" />
                        <Column field="capacity" title={"Capacity"} className="text-center" />
                        <Column field="isGeneralAdmission" title={"GA?"} />
                    </Grid>
                </Dialog>
            }
        </div>
    );
}